import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useApi, ApiError } from '../api/client';
import './SharePinModal.css';
import { formatTime } from '../api/formatDate';

/**
 * SharePinModal — sub-modal for setting or verifying the share-mode PIN.
 *
 * Two modes:
 *   • 'set'    — kaimahi sets (or rotates) their PIN before entering share
 *                mode for the first time. PUT /api/v1/me/share-pin.
 *   • 'verify' — kaimahi exits share mode. POST /api/v1/me/share-pin/verify.
 *                Lockout enforced server-side; 423 returns surface as a
 *                "try again at <time>" message.
 *
 * Same modal idiom as the wizard and share view (createPortal + blurred
 * backdrop), but smaller and locked down — for the verify case the
 * backdrop click and ESC are intentionally disabled so a whānau can't tap
 * out.
 */

type Mode = 'set' | 'verify';

type Props = {
  mode: Mode;
  /** Called on successful set / verify. */
  onSuccess: () => void;
  /**
   * Called when the kaimahi explicitly dismisses (e.g. abandons setup).
   * For 'verify' mode this is also wired to the "Cancel" button so the
   * caller can let the kaimahi back out of the exit attempt without
   * leaving share mode.
   */
  onCancel: () => void;
};

const PIN_LENGTH_MIN = 4;
const PIN_LENGTH_MAX = 6;

export function SharePinModal({ mode, onSuccess, onCancel }: Props) {
  const { t } = useTranslation();
  const callApi = useApi();

  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'working' }
    | { kind: 'locked'; until: string }
    | { kind: 'error'; message: string; failedAttempts?: number }
  >({ kind: 'idle' });

  const inputRef = useRef<HTMLInputElement>(null);

  // Lock body scroll while open + autofocus the input.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const onSubmit = async () => {
    const trimmed = pin.trim();
    if (!/^[0-9]+$/.test(trimmed) || trimmed.length < PIN_LENGTH_MIN || trimmed.length > PIN_LENGTH_MAX) {
      setState({
        kind: 'error',
        message: t('sharePin.errInvalid'),
      });
      return;
    }

    if (mode === 'set') {
      if (confirmPin !== trimmed) {
        setState({ kind: 'error', message: t('sharePin.errMismatch') });
        return;
      }
    }

    setState({ kind: 'working' });
    try {
      if (mode === 'set') {
        await callApi('/me/share-pin', {
          method: 'PUT',
          body: JSON.stringify({ pin: trimmed }),
          headers: { 'content-type': 'application/json' },
        });
      } else {
        await callApi('/me/share-pin/verify', {
          method: 'POST',
          body: JSON.stringify({ pin: trimmed }),
          headers: { 'content-type': 'application/json' },
        });
      }
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423) {
          // Locked. Body should carry locked_until.
          const lockedUntil =
            typeof err.body === 'object' &&
            err.body !== null &&
            'locked_until' in err.body
              ? String((err.body as { locked_until: unknown }).locked_until ?? '')
              : '';
          setState({ kind: 'locked', until: lockedUntil });
          setPin('');
          return;
        }
        if (err.status === 401) {
          const failed =
            typeof err.body === 'object' &&
            err.body !== null &&
            'failed_attempts' in err.body
              ? Number((err.body as { failed_attempts: unknown }).failed_attempts ?? 0)
              : undefined;
          setState({
            kind: 'error',
            message: t('sharePin.errWrong'),
            failedAttempts: failed,
          });
          setPin('');
          return;
        }
        setState({
          kind: 'error',
          message: `${String(err.status)} — ${err.message}`,
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setState({ kind: 'error', message });
    }
  };

  const overlay = (
    <div
      className="pin-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pin-title"
      // Intentional: clicking the backdrop does NOT dismiss. The only way
      // out of verify mode is the correct PIN; the only way out of setup
      // is the Cancel button.
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      <div className="pin-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h2 id="pin-title" className="pin-modal__title">
          {mode === 'set' ? t('sharePin.setupTitle') : t('sharePin.verifyTitle')}
        </h2>
        <p className="pin-modal__subtitle">
          {mode === 'set'
            ? t('sharePin.setupSubtitle')
            : t('sharePin.verifySubtitle')}
        </p>

        <form
          className="pin-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <label htmlFor="pin-input" className="pin-modal__label">
            {t('sharePin.pinLabel')}
          </label>
          <input
            id="pin-input"
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            pattern="[0-9]*"
            maxLength={PIN_LENGTH_MAX}
            value={pin}
            onChange={(e) => {
              setPin(e.target.value.replace(/\D/g, ''));
              if (state.kind === 'error') setState({ kind: 'idle' });
            }}
            disabled={state.kind === 'working' || state.kind === 'locked'}
            className="pin-modal__input"
          />

          {mode === 'set' && (
            <>
              <label htmlFor="pin-confirm" className="pin-modal__label">
                {t('sharePin.confirmLabel')}
              </label>
              <input
                id="pin-confirm"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                pattern="[0-9]*"
                maxLength={PIN_LENGTH_MAX}
                value={confirmPin}
                onChange={(e) => {
                  setConfirmPin(e.target.value.replace(/\D/g, ''));
                  if (state.kind === 'error') setState({ kind: 'idle' });
                }}
                disabled={state.kind === 'working'}
                className="pin-modal__input"
              />
            </>
          )}

          {state.kind === 'error' && (
            <p className="pin-modal__error" role="alert">
              {state.message}
              {typeof state.failedAttempts === 'number' && (
                <>
                  {' · '}
                  {t('sharePin.attemptsLeft', {
                    remaining: Math.max(0, 5 - state.failedAttempts),
                  })}
                </>
              )}
            </p>
          )}

          {state.kind === 'locked' && (
            <p className="pin-modal__locked" role="alert">
              {t('sharePin.locked', {
                time: state.until
                  ? formatTime(state.until)
                  : '?',
              })}
            </p>
          )}

          <div className="pin-modal__actions">
            <button
              type="button"
              className="auth-button auth-button--secondary"
              onClick={onCancel}
              disabled={state.kind === 'working'}
            >
              {t('sharePin.cancel')}
            </button>
            <button
              type="submit"
              className="auth-button auth-button--primary"
              disabled={
                state.kind === 'working' ||
                state.kind === 'locked' ||
                pin.length < PIN_LENGTH_MIN
              }
            >
              {state.kind === 'working'
                ? t('sharePin.working')
                : mode === 'set'
                  ? t('sharePin.setButton')
                  : t('sharePin.verifyButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default SharePinModal;
